import type { FinancialPlan, Account, Income, Expense } from '../store/financialPlanStore'

interface SimulationParams {
  currentAge: number
  retirementAge: number
  endAge: number
  initialPortfolio: number
  stocksPercent: number
  bondsPercent: number
  cashPercent: number
  income: Income[]
  expenses: Expense[]
  accounts: Account[]
  inflationRate: number
}

interface SimulationResult {
  successRate: number
  medianEndingValue: number
  percentile10: number
  percentile90: number
  yearlyData: YearlyProjection[]
  allSimulations: number[][]
}

interface YearlyProjection {
  year: number
  age: number
  percentile10: number
  median: number
  percentile90: number
  income: number
  expenses: number
  netCashFlow: number
}

export class MonteCarloSimulation {
  private readonly NUM_SIMULATIONS = 1000
  private readonly STOCK_MEAN_RETURN = 0.10 // 10% average
  private readonly STOCK_STD_DEV = 0.18 // 18% volatility
  private readonly BOND_MEAN_RETURN = 0.05 // 5% average
  private readonly BOND_STD_DEV = 0.06 // 6% volatility
  private readonly CASH_RETURN = 0.03 // 3% for cash
  
  /**
   * Run Monte Carlo simulation
   */
  runSimulation(plan: FinancialPlan): SimulationResult {
    const client = plan.familyMembers.find(m => m.relationship === 'client')
    if (!client) {
      throw new Error('Client information not found')
    }
    
    const currentAge = this.calculateAge(client.dateOfBirth)
    const retirementAge = client.retirementAge || 65
    const endAge = 100
    
    const totalPortfolio = this.calculateTotalPortfolio(plan.accounts)
    const avgStocksPercent = this.calculateAvgAllocation(plan.accounts, 'stocks')
    const avgBondsPercent = this.calculateAvgAllocation(plan.accounts, 'bonds')
    const avgCashPercent = this.calculateAvgAllocation(plan.accounts, 'cash')
    
    const params: SimulationParams = {
      currentAge,
      retirementAge,
      endAge,
      initialPortfolio: totalPortfolio,
      stocksPercent: avgStocksPercent,
      bondsPercent: avgBondsPercent,
      cashPercent: avgCashPercent,
      income: plan.income,
      expenses: plan.expenses,
      accounts: plan.accounts,
      inflationRate: 0.025, // 2.5% default
    }
    
    return this.simulate(params)
  }
  
  private simulate(params: SimulationParams): SimulationResult {
    const numYears = params.endAge - params.currentAge + 1
    const allSimulations: number[][] = []
    
    // Run simulations
    for (let sim = 0; sim < this.NUM_SIMULATIONS; sim++) {
      const simulation = this.runSingleSimulation(params, numYears)
      allSimulations.push(simulation)
    }
    
    // Calculate statistics
    const successRate = this.calculateSuccessRate(allSimulations)
    const yearlyData = this.calculateYearlyStatistics(allSimulations, params)
    const endingValues = allSimulations.map(sim => sim[sim.length - 1])
    
    return {
      successRate,
      medianEndingValue: this.percentile(endingValues, 0.5),
      percentile10: this.percentile(endingValues, 0.1),
      percentile90: this.percentile(endingValues, 0.9),
      yearlyData,
      allSimulations,
    }
  }
  
  private runSingleSimulation(params: SimulationParams, numYears: number): number[] {
    const portfolioValues: number[] = []
    let currentPortfolio = params.initialPortfolio
    
    for (let year = 0; year < numYears; year++) {
      const currentAge = params.currentAge + year
      
      // Calculate investment returns
      const returns = this.calculateReturns(
        params.stocksPercent,
        params.bondsPercent,
        params.cashPercent
      )
      
      // Apply returns
      currentPortfolio *= (1 + returns)
      
      // Calculate income for this year
      const income = this.calculateYearlyIncome(params.income, currentAge, year, params.inflationRate)
      
      // Calculate expenses for this year
      const expenses = this.calculateYearlyExpenses(params.expenses, currentAge, year, params.inflationRate)
      
      // Net cash flow
      const netCashFlow = income - expenses
      
      // If negative, withdraw from portfolio
      if (netCashFlow < 0) {
        const withdrawal = Math.min(Math.abs(netCashFlow), currentPortfolio)
        currentPortfolio -= withdrawal
      } else {
        // If positive, add to portfolio
        currentPortfolio += netCashFlow
      }
      
      // Apply RMDs if applicable (starting at age 73)
      if (currentAge >= 73) {
        const rmd = this.calculateRMD(currentPortfolio, currentAge)
        currentPortfolio -= rmd
      }
      
      // Ensure portfolio doesn't go negative
      currentPortfolio = Math.max(0, currentPortfolio)
      
      portfolioValues.push(currentPortfolio)
    }
    
    return portfolioValues
  }
  
  private calculateReturns(
    stocksPercent: number,
    bondsPercent: number,
    cashPercent: number
  ): number {
    // Generate random returns using normal distribution
    const stockReturn = this.normalRandom(this.STOCK_MEAN_RETURN, this.STOCK_STD_DEV)
    const bondReturn = this.normalRandom(this.BOND_MEAN_RETURN, this.BOND_STD_DEV)
    const cashReturn = this.CASH_RETURN
    
    // Weighted average
    return (
      (stocksPercent / 100) * stockReturn +
      (bondsPercent / 100) * bondReturn +
      (cashPercent / 100) * cashReturn
    )
  }
  
  private calculateYearlyIncome(
    income: Income[],
    age: number,
    yearsFromNow: number,
    inflationRate: number
  ): number {
    let totalIncome = 0
    
    for (const inc of income) {
      // Check if income is active at this age
      if (age >= inc.startAge && (!inc.endAge || age <= inc.endAge)) {
        let amount = inc.amount
        
        // Apply growth rate
        const yearsActive = age - inc.startAge
        amount *= Math.pow(1 + inc.growthRate / 100, yearsActive)
        
        // Apply COLA if applicable (for pension/SS)
        if (inc.cola) {
          amount *= Math.pow(1 + inc.cola / 100, yearsActive)
        }
        
        totalIncome += amount
      }
    }
    
    return totalIncome
  }
  
  private calculateYearlyExpenses(
    expenses: Expense[],
    age: number,
    yearsFromNow: number,
    baseInflationRate: number
  ): number {
    let totalExpenses = 0
    
    for (const exp of expenses) {
      // Check if expense is active at this age
      if (
        (!exp.startAge || age >= exp.startAge) &&
        (!exp.endAge || age <= exp.endAge)
      ) {
        let amount = exp.amount
        
        // Apply category-specific inflation
        amount *= Math.pow(1 + exp.inflationRate / 100, yearsFromNow)
        
        totalExpenses += amount
      }
    }
    
    return totalExpenses
  }
  
  private calculateRMD(portfolioValue: number, age: number): number {
    // RMD distribution table (simplified)
    const distributionPeriods: { [key: number]: number } = {
      73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0,
      79: 21.1, 80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8,
      85: 16.0, 86: 15.2, 87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2,
    }
    
    const period = distributionPeriods[age] || 12.2
    return portfolioValue / period
  }
  
  private calculateSuccessRate(simulations: number[][]): number {
    let successful = 0
    
    for (const sim of simulations) {
      // Success = portfolio never goes to zero
      const neverBroke = sim.every(value => value > 0)
      if (neverBroke) successful++
    }
    
    return (successful / simulations.length) * 100
  }
  
  private calculateYearlyStatistics(
    simulations: number[][],
    params: SimulationParams
  ): YearlyProjection[] {
    const numYears = simulations[0].length
    const yearlyData: YearlyProjection[] = []
    
    for (let year = 0; year < numYears; year++) {
      const age = params.currentAge + year
      const values = simulations.map(sim => sim[year])
      
      const income = this.calculateYearlyIncome(params.income, age, year, params.inflationRate)
      const expenses = this.calculateYearlyExpenses(params.expenses, age, year, params.inflationRate)
      
      yearlyData.push({
        year: new Date().getFullYear() + year,
        age,
        percentile10: this.percentile(values, 0.1),
        median: this.percentile(values, 0.5),
        percentile90: this.percentile(values, 0.9),
        income,
        expenses,
        netCashFlow: income - expenses,
      })
    }
    
    return yearlyData
  }
  
  private calculateTotalPortfolio(accounts: Account[]): number {
    return accounts.reduce((sum, account) => sum + account.balance, 0)
  }
  
  private calculateAvgAllocation(accounts: Account[], type: 'stocks' | 'bonds' | 'cash'): number {
    const totalValue = this.calculateTotalPortfolio(accounts)
    if (totalValue === 0) return 0
    
    const weighted = accounts.reduce((sum, account) => {
      const percent = type === 'stocks' ? account.stocksPercent :
                     type === 'bonds' ? account.bondsPercent :
                     account.cashPercent
      return sum + (account.balance * percent)
    }, 0)
    
    return (weighted / totalValue) || 0
  }
  
  private calculateAge(dateOfBirth: string): number {
    const birth = new Date(dateOfBirth)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    
    return age
  }
  
  private normalRandom(mean: number, stdDev: number): number {
    // Box-Muller transform for normal distribution
    const u1 = Math.random()
    const u2 = Math.random()
    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
    return mean + stdDev * z0
  }
  
  private percentile(values: number[], p: number): number {
    const sorted = [...values].sort((a, b) => a - b)
    const index = Math.ceil(sorted.length * p) - 1
    return sorted[Math.max(0, index)]
  }
}

export const monteCarloSimulation = new MonteCarloSimulation()
